import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import TrackTile from "../common/TrackSelectionTile";
import SelectorTileGrid from "../common/SelectorTileGrid";
import miniTrackImage from "@/assets/images/miniTrack_.jpeg";
import mainTrackImage from "@/assets/images/main_track_.jpg";
import arcadeImage from "@/assets/images/upsell-arcade.png";
import { ROUTES, TRACKS } from "@/consts";
import {
  TTrackTypes,
  TMetaObjectBannerSection,
  TMetaObjectMainTrackConfig,
  TMetaObjectMiniTrackConfig,
  TUpsellModalConfig,
  TMetaObjectSeatsAndRacesConfig,
} from "../../types";
import { useTrackContext } from "@/state/TrackContext";
import { useUrlParams } from "@/hooks/useUrlParams";
import { useRegisterBottomSummary } from "@/hooks/useRegisterBottomSummary";

import { useLocation, useNavigate } from "react-router-dom";
import { resolveBookingConfig, MetaObjectConfigData } from "@/shared/utils/metaobjectOperations";
import AttractionGapWarningPopup from "../common/AttractionGapWarningPopup";
import { DynamicFullHeightContainer } from "../common/FullHeightContainer";
import getCartData from "@/shared/utils/getCartData";
import { LoadingAnimationURL } from "@/consts/assetUrls";
import { BookingAnalytics } from "@/shared/utils/bookingAnalytics";
import { usePathwayStyling } from "@/hooks/usePathwayStyling";
import { isMainBookingPath } from "@/shared/utils/bookingUrl";
import { BATTLEKART_ENABLED, shouldSkipExperienceSelector } from "@/consts/featureFlags";

const MIN_TILE_HEIGHT = 120;
const MAX_TILE_HEIGHT_VIEWPORT_RATIO = 0.45;
const TITLE_HEADING_HEIGHT = 100;
const GRID_VERTICAL_MARGINS = 20;
const TILE_GAP = 10;
const MOBILE_FOOTER_HEIGHT = 64;
const DESKTOP_FOOTER_HEIGHT = 100;
const MOBILE_BREAKPOINT = 767;

const currentHref = window.location.pathname;
const TrackSelection = () => {
  const navigate = useNavigate();
  const { getUrlParams, navigateWithParams } = useUrlParams();
  const { titleStyle, subtitleStyle, visibility } = usePathwayStyling("trackSelection");
  const {
    numberOfSeats: paramsNumberOfSeats,
    numberOfRaces: paramsNumberOfRaces,
    ageGroup: paramsAgeGroup,
    homepage: paramsHomepage,
    redirect: paramsRedirect,
    selectedRoute: paramsSelectedRoute,
  } = getUrlParams();
  const location = useLocation();

  const {
    dispatch,
    productRenderedLocation,
    trackSelectionOption: { title },
    windowWidth,
    metaObjectTrackSelectionConfig,
    appHostedAt,
    isFirstVisit,
    showArcadeBanner,
    headlessCartInfo,
    isAnyItemsAvailableInCart,
  } = useTrackContext();

  // Only show the full-screen loader on a genuine cold start (config not fetched yet).
  // On return visits the config is already in context, so we render the experience
  // tiles immediately and let them ride the page slide/fade-in. Otherwise a spinner
  // would occupy the transition and the real content would pop in abruptly after the
  // background fetch resolves, making the root entrance feel faster than other routes.
  const hasTrackSelectionConfig = Boolean(metaObjectTrackSelectionConfig?.racingGamesTrack || metaObjectTrackSelectionConfig?.mainTrack);
  const [loading, setLoading] = useState<boolean>(!hasTrackSelectionConfig);

  const [selectedTrack, setSelectedTrack] = useState<TTrackTypes>(title === "ARCADE" ? ("RACING_GAMES_TRACK" as TTrackTypes) : (title as TTrackTypes));
  const [containerHeightPx, setContainerHeightPx] = useState(0);

  useEffect(() => {
    if (paramsSelectedRoute === undefined) {
      dispatch({
        type: "SET_SELECTED_TRACK",
        payload: {
          selectedTrackTitle: "RACING_GAMES_TRACK",
          trackBanner: mainTrackImage,
        },
      });
      setSelectedTrack("RACING_GAMES_TRACK" as TTrackTypes);
    }
  }, [paramsSelectedRoute, dispatch]);
  const [isAttractionGapWarningShowing, setIsAttractionGapWarningShowing] = useState(false);
  const [pendingTrackSelection, setPendingTrackSelection] = useState<{ track: TTrackTypes; imageSrc: string } | null>(null);

  const toNavigation = useCallback(() => {
    switch (selectedTrack) {
      case TRACKS.MAIN_TRACK:
        if (paramsNumberOfSeats && paramsNumberOfRaces && paramsHomepage && paramsHomepage === "false") {
          return ROUTES.MAIN_TRACK.DATE_TIME;
        } else {
          return ROUTES.RACING_GAMES_TRACK.INDEX;
        }
      case TRACKS.MINI_TRACK:
        return ROUTES.MINI_TRACK.SEATS_RACES;
      case TRACKS.JUNIOR_TRACK:
        if (paramsNumberOfSeats && paramsNumberOfRaces && paramsHomepage && paramsHomepage === "false") {
          return ROUTES.JUNIOR_TRACK.DATE_TIME;
        } else {
          return ROUTES.JUNIOR_TRACK.SEATS_RACES;
        }
      case TRACKS.BATTLE_KART_TRACK:
        return ROUTES.BATTLE_KART_TRACK.PLAYERS_SESSIONS;
      case TRACKS.ARCADE:
        return ROUTES.ARCADE.ARCADE_CARD;
    }
  }, [paramsHomepage, paramsNumberOfRaces, paramsNumberOfSeats, selectedTrack]);

  const getNavigationForTrack = useCallback(
    (track: TTrackTypes) => {
      switch (track) {
        case TRACKS.MAIN_TRACK:
          if (paramsNumberOfSeats && paramsNumberOfRaces && paramsHomepage && paramsHomepage === "false") {
            return ROUTES.MAIN_TRACK.DATE_TIME;
          } else {
            return ROUTES.RACING_GAMES_TRACK.INDEX;
          }
        case TRACKS.MINI_TRACK:
          return ROUTES.MINI_TRACK.SEATS_RACES;
        case TRACKS.JUNIOR_TRACK:
          if (paramsNumberOfSeats && paramsNumberOfRaces && paramsHomepage && paramsHomepage === "false") {
            return ROUTES.JUNIOR_TRACK.DATE_TIME;
          } else {
            return ROUTES.JUNIOR_TRACK.SEATS_RACES;
          }
        case TRACKS.BATTLE_KART_TRACK:
          return ROUTES.BATTLE_KART_TRACK.PLAYERS_SESSIONS;
        case TRACKS.RACING_GAMES_TRACK:
          return ROUTES.RACING_GAMES_TRACK.INDEX;
        case TRACKS.ARCADE:
          return ROUTES.ARCADE.ARCADE_CARD;
        default:
          return ROUTES.RACING_GAMES_TRACK.INDEX;
      }
    },
    [paramsHomepage, paramsNumberOfRaces, paramsNumberOfSeats],
  );

  const handleDoubleRedirect = (url: string) => {
    return navigate(url);
  };

  // Helper to check if cart has products of a specific type
  const checkCartForConflict = useCallback(async (clickedTrack: TTrackTypes): Promise<boolean> => {
    // Only check for Racing vs BattleKart conflicts
    const isClickingRacing = clickedTrack === "RACING_GAMES_TRACK";
    const isClickingBattleKart = clickedTrack === "BATTLE_KART_TRACK";

    if (!isClickingRacing && !isClickingBattleKart) {
      return false;
    }

    try {
      const cartData = await getCartData(productRenderedLocation, headlessCartInfo);
      if (!cartData || !cartData.cart) {
        return false;
      }

      let hasBattleKart = false;
      let hasRacing = false;

      if (productRenderedLocation === "external") {
        // Headless cart structure
        const lines = cartData.cart?.lines?.edges;
        if (lines && lines.length > 0) {
          hasBattleKart = lines.some((edge: any) => edge.node?.merchandise?.product?.productType === "BattleKart");
          hasRacing = lines.some((edge: any) => {
            const productType = edge.node?.merchandise?.product?.productType;
            return ["Main Track", "Mini Track", "Junior Track"].includes(productType);
          });
        }
      } else {
        // Shopify internal cart structure
        const items = cartData.cart?.items;
        if (items && items.length > 0) {
          hasBattleKart = items.some((item: any) => item.product_type === "BattleKart");
          hasRacing = items.some((item: any) => ["Main Track", "Mini Track", "Junior Track"].includes(item.product_type));
        }
      }

      // Adding more of an attraction that is already in the cart introduces no new
      // BK + HK combination, so don't warn (e.g. Racing in cart, Racing selected
      // again; or BK in cart, BK selected again).
      if ((isClickingRacing && hasRacing) || (isClickingBattleKart && hasBattleKart)) {
        return false;
      }

      // Only warn when Racing (HK) and BK
      if ((isClickingRacing && hasBattleKart) || (isClickingBattleKart && hasRacing)) {
        return true;
      }

      return false;
    } catch (error) {
      console.error("Error checking cart for conflict:", error);
      return false;
    }
  }, [productRenderedLocation, headlessCartInfo]);

  const proceedWithTrackSelection = useCallback(
    (track: TTrackTypes, imageSrc: string) => {
      setSelectedTrack(track);
      BookingAnalytics.trackSelected(track);
      dispatch({
        type: "SET_SELECTED_TRACK",
        payload: {
          selectedTrackTitle: track,
          trackBanner: imageSrc,
        },
      });

      const route = getNavigationForTrack(track);
      if (route) {
        return navigateWithParams(route);
      }
    },
    [dispatch, getNavigationForTrack, navigateWithParams],
  );

  // The mobile footer lines its Next/Checkout pill up with these tiles' outer edges.
  const tileGridRef = useRef<HTMLDivElement>(null);

  // A single tap on a tile commits immediately (see handleTrackSelection wired to each
  // tile), so this screen has no Next step and omits nextInfo. The nav config is still
  // registered so the footer can surface Checkout when the cart already has items.
  const bottomSummaryNavConfig = useMemo(
    () => ({
      previousInfo: { pageName: "Experience", pagePath: ROUTES.INDEX, isActive: true },
      mobileNavVariant: "experience" as const,
      actionAlignRef: tileGridRef,
    }),
    [],
  );

  useRegisterBottomSummary(bottomSummaryNavConfig);

  const handleTrackSelection = async (track: TTrackTypes, imageSrc: string) => {
    // Only check for conflicts if there are items in cart
    if (isAnyItemsAvailableInCart) {
      const hasConflict = await checkCartForConflict(track);

      if (hasConflict) {
        // Store the pending selection and show warning popup
        setPendingTrackSelection({ track, imageSrc });
        setIsAttractionGapWarningShowing(true);
        return;
      }
    }

    // No conflict, proceed with navigation
    proceedWithTrackSelection(track, imageSrc);
  };

  // Handle popup close - proceed with navigation
  const handleCloseAttractionGapWarning = () => {
    setIsAttractionGapWarningShowing(false);

    if (pendingTrackSelection) {
      proceedWithTrackSelection(pendingTrackSelection.track, pendingTrackSelection.imageSrc);
      setPendingTrackSelection(null);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Check if we're on the root path - only do automatic navigation if we are
        const currentPath = location.pathname;

        if (currentPath !== "/") {
          setLoading(false);
          return;
        }
        // Resolve config inline-first (data-booking-config) with metaobject
        // fallback, so an inline config is never overwritten by a direct
        // metaobject fetch here.
        const configData = await resolveBookingConfig();
        const { bannerSection, mainTrackConfig, miniTrackConfig, upsellModalConfig, juniorTrackConfig, battleKartConfig } =
          (configData ?? {}) as Partial<MetaObjectConfigData>;

        if (bannerSection && mainTrackConfig && miniTrackConfig && upsellModalConfig && juniorTrackConfig && battleKartConfig) {
          dispatch({
            type: "SET_METAOJECT_BATTLE_KART_TRACK_CONFIG",
            payload: {
              metaObjectBattleKartTrackSectionConfig: battleKartConfig as TMetaObjectSeatsAndRacesConfig,
            },
          });

          dispatch({
            type: "SET_METAOBJECT_TRACK_SELECTION_CONFIG",
            payload: {
              metaObjectTrackSelectionConfig: bannerSection as TMetaObjectBannerSection,
            },
          });

          dispatch({
            type: "SET_METAOJECT_MAIN_TRACK_CONFIG",
            payload: {
              metaObjectMainTrackSectionConfig: mainTrackConfig as TMetaObjectMainTrackConfig,
            },
          });

          dispatch({
            type: "SET_METAOJECT_MINI_TRACK_CONFIG",
            payload: {
              metaObjectMiniTrackSectionConfig: miniTrackConfig as TMetaObjectMiniTrackConfig,
            },
          });

          dispatch({
            type: "SET_METAOJECT_UPSELL_MODAL_CONFIG",
            payload: {
              upsellModalConfig: upsellModalConfig as TUpsellModalConfig,
            },
          });

          dispatch({
            type: "SET_METAOJECT_JUNIOR_TRACK_CONFIG",
            payload: {
              metaObjectJuniorTrackSectionConfig: juniorTrackConfig as TMetaObjectSeatsAndRacesConfig,
            },
          });
          const { seatsAndRacesSection } = mainTrackConfig;
          const { seatsAndRacesSection: juniorTrackSeatsAndRacesSection } = juniorTrackConfig;

          if (seatsAndRacesSection) {
            if (paramsNumberOfSeats && paramsNumberOfRaces) {
              dispatch({
                type: "SET_RACES_SEATS",
                payload: {
                  selectedTrack: "MAIN_TRACK",
                  racesSeats: {
                    numberOfRaces: parseInt(paramsNumberOfRaces),
                    numberOfSeats: parseInt(paramsNumberOfSeats),
                  },
                },
              });
              if (paramsAgeGroup) {
                dispatch({
                  type: "SET_AGE_GROUP",
                  payload: {
                    selectedTrack: "MAIN_TRACK",
                    ageGroup: "Under 12",
                  },
                });
              }
            } else {
              dispatch({
                type: "SET_RACES_SEATS",
                payload: {
                  selectedTrack: "MAIN_TRACK",
                  racesSeats: {
                    numberOfRaces: seatsAndRacesSection.raceQuantity,
                    numberOfSeats: seatsAndRacesSection.seatsQuantity,
                  },
                },
              });
            }
          }

          if (juniorTrackSeatsAndRacesSection) {
            if (paramsNumberOfSeats && paramsNumberOfRaces) {
              dispatch({
                type: "SET_RACES_SEATS_JUNIOR_TRACK",
                payload: {
                  racesSeats: {
                    numberOfRaces: parseInt(paramsNumberOfRaces),
                    numberOfSeats: parseInt(paramsNumberOfSeats),
                  },
                },
              });
            } else {
              dispatch({
                type: "SET_RACES_SEATS_JUNIOR_TRACK",
                payload: {
                  racesSeats: {
                    numberOfRaces: juniorTrackSeatsAndRacesSection.raceQuantity,
                    numberOfSeats: juniorTrackSeatsAndRacesSection.seatsQuantity,
                  },
                },
              });
            }
          }

          if (miniTrackConfig.seatsAndRacesSection) {
            const { raceQuantity, seatsQuantity } = miniTrackConfig.seatsAndRacesSection;
            if (raceQuantity && seatsQuantity) {
              dispatch({
                type: "SET_RACES_SEATS_MINI_TRACK",
                payload: {
                  selectedTrack: "MINI_TRACK",
                  racesSeats: {
                    numberOfRaces: raceQuantity,
                    numberOfSeats: seatsQuantity,
                  },
                },
              });
            }
          }

          if (battleKartConfig.seatsAndRacesSection) {
            const { raceQuantity, seatsQuantity } = battleKartConfig.seatsAndRacesSection;
            if (raceQuantity && seatsQuantity) {
              dispatch({
                type: "SET_RACES_SEATS_BATTLE_KART_TRACK",
                payload: {
                  racesSeats: {
                    numberOfRaces: raceQuantity,
                    numberOfSeats: seatsQuantity,
                  },
                },
              });
            }
          }

          if (location.pathname === "/" && isFirstVisit && paramsRedirect === "true") {
            dispatch({
              type: "SET_IS_FIRST_VISIT",
              payload: {
                isFirstVisit: false,
              },
            });

            if (isMainBookingPath(currentHref)) {
              dispatch({
                type: "SET_SELECTED_TRACK",
                payload: {
                  selectedTrackTitle: "MAIN_TRACK",
                  trackBanner: mainTrackImage,
                },
              });
              navigate(ROUTES.RACING_GAMES_TRACK.INDEX);
            } else if (currentHref.endsWith("/kids-go-karting-track") || /\d{7}mi$/.test(currentHref)) {
              dispatch({
                type: "SET_SELECTED_TRACK",
                payload: {
                  selectedTrackTitle: "MINI_TRACK",
                  trackBanner: miniTrackImage,
                },
              });
              navigate(ROUTES.MINI_TRACK.SEATS_RACES);
            } else if (currentHref.endsWith("/junior-track") || /\d{6}jt$/.test(currentHref)) {
              dispatch({
                type: "SET_SELECTED_TRACK",
                payload: {
                  selectedTrackTitle: "JUNIOR_TRACK",
                  trackBanner: mainTrackImage,
                },
              });
              navigate(ROUTES.JUNIOR_TRACK.SEATS_RACES);
            } else if (currentHref.endsWith("/pages/battle-kart-booking") || /\d{6}bk$/.test(currentHref)) {
              dispatch({
                type: "SET_SELECTED_TRACK",
                payload: {
                  selectedTrackTitle: "BATTLE_KART_TRACK",
                  trackBanner: mainTrackImage,
                },
              });
              navigate(ROUTES.BATTLE_KART_TRACK.PLAYERS_SESSIONS);
            }
          }
        }

        // Only do automatic navigation if we're on the root path
        if (currentPath === "/" && paramsNumberOfSeats && paramsNumberOfRaces) {
          if (paramsHomepage && paramsHomepage === "false" && selectedTrack === "MAIN_TRACK") {
            navigateWithParams(ROUTES.MAIN_TRACK.DATE_TIME);
          } else if (paramsHomepage && paramsHomepage === "false" && selectedTrack === "JUNIOR_TRACK") {
            navigateWithParams(ROUTES.JUNIOR_TRACK.DATE_TIME);
          }
        }

        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.error(error);
      }
    };

    fetchData();
  }, [
    dispatch,
    location.pathname,
    navigate,
    paramsHomepage,
    paramsNumberOfRaces,
    paramsNumberOfSeats,
    selectedTrack,
    toNavigation,
    isFirstVisit,
    paramsRedirect,
    paramsAgeGroup,
    navigateWithParams,
  ]);

	// BK closed => skip experience selection since only racing is left
  // (still mounts: fetchData above seeds the seats/races config defaults, and
  // its legacy ?redirect=true navigation gets to run first, before loading clears)
  const isForwardingToRacing =
    shouldSkipExperienceSelector({ isAnyItemsAvailableInCart, showArcadeBanner, appHostedAt }) && location.pathname === "/";

  useLayoutEffect(() => {
    if (loading || !isForwardingToRacing) return;
    navigateWithParams(ROUTES.RACING_GAMES_TRACK.INDEX, undefined, true);
  }, [loading, isForwardingToRacing, navigateWithParams]);

  const { mainTrack, miniTrack, juniorTrack, racingGamesTrack, battleKartTrack, arcadeBanner, trackSelectionScreen, addAnotherAttractionScreen } =
    metaObjectTrackSelectionConfig;

  const showRacingGames = Boolean(racingGamesTrack) && visibility.racingGames !== false;
  const showBattleKart = BATTLEKART_ENABLED && Boolean(battleKartTrack) && visibility.battleKart !== false;
  const showArcade = Boolean(arcadeBanner && showArcadeBanner) && visibility.arcade !== false;

  const hasTitleHeading = !(isAnyItemsAvailableInCart && showArcadeBanner) && !!(trackSelectionScreen?.title || trackSelectionScreen?.subtitle);
  const isMobile = windowWidth <= MOBILE_BREAKPOINT;
  // Below the desktop 2-column grid breakpoint (1024px) the tiles are stacked vertically,
  // so let them grow to fill the available height on phones AND tablets/iPad.
  const useTileFill = windowWidth < 1024;

  // Always reserve space for the footer (Next/Checkout button area) so the layout
  // doesn't jump when buttons appear/disappear based on selection state.
  const footerHeight = useMemo(() => {
    return isMobile ? MOBILE_FOOTER_HEIGHT : DESKTOP_FOOTER_HEIGHT;
  }, [isMobile]);

  const numTiles = useMemo(() => {
    let count = 0;
    if (showRacingGames) count++;
    if (showBattleKart) count++;
    if (showArcade) count++;
    return count;
  }, [showRacingGames, showBattleKart, showArcade]);

  const tileHeight = useMemo(() => {
    if (!isMobile || numTiles === 0 || containerHeightPx === 0) return undefined;
    const headingOverhead = hasTitleHeading ? TITLE_HEADING_HEIGHT : 0;
    const gapsTotal = Math.max(0, numTiles - 1) * TILE_GAP;
    const available = containerHeightPx - headingOverhead - GRID_VERTICAL_MARGINS - gapsTotal;
    const calculated = available / numTiles;
    const viewportMax = Math.floor(window.innerHeight * MAX_TILE_HEIGHT_VIEWPORT_RATIO);
    return Math.min(viewportMax, Math.max(MIN_TILE_HEIGHT, Math.floor(calculated)));
  }, [isMobile, numTiles, containerHeightPx, hasTitleHeading]);

  // Desktop root grid: keep the original 80px/40px vertical margins whenever the
  // measured container can fit the natural tile row (380px tile + description).
  // Only when it doesn't fit (short viewport and/or tall theme chrome) do we
  // compress the margins — trading margin space for tile size so the container
  // still never scrolls. containerHeightPx is 0 before the first measurement, in
  // which case we optimistically keep the full margins (the historical layout).
  const ROOT_GRID_NATURAL_ROW_PX = 438;
  const ROOT_GRID_FULL_MARGINS_PX = 120;
  const ROOT_GRID_HEADING_BLOCK_PX = 76; // lg:mt-40 + title/subtitle lines, measured
  const rootGridHasRoom = containerHeightPx === 0 || containerHeightPx >= (hasTitleHeading ? ROOT_GRID_HEADING_BLOCK_PX : 0) + ROOT_GRID_FULL_MARGINS_PX + ROOT_GRID_NATURAL_ROW_PX;

  return (
    <>
      {loading || isForwardingToRacing ? (
        <>
          <div className="container tw-flex tw-justify-center tw-items-center tw-mt-[100px] tw-mb-[100px]">
            <img src={LoadingAnimationURL} alt="Loading" className="tw-w-[230px] md:tw-w-[300px] tw-pointer-events-none" />
          </div>
        </>
      ) : (
        <>
          <div className={`${productRenderedLocation === "external" ? "tw-container tw-mx-auto" : "container mx-auto"}`}>
            {/* Add Another Attraction Heading - Only show when returning from session selection flow */}
            {isAnyItemsAvailableInCart && showArcadeBanner && (
              <div className="tw-text-center tw-mb-[8px] tw-mt-[20px] lg:tw-mt-[50px]">
                <h1
                  data-hlc-field="banner_general.aa_title"
                  className="tw-text-white tw-text-[12px] sm:tw-text-[20px] lg:tw-text-[24px] tw-font-extrabold tw-px-[4px]"
                >
                  {addAnotherAttractionScreen?.title || "Would you like to add another attraction?"}
                </h1>
                {addAnotherAttractionScreen?.subtitle && (
                  <p
                    data-hlc-field="banner_general.aa_subtitle"
                    className="tw-text-white tw-text-[11px] sm:tw-text-[14px] lg:tw-text-[16px] tw-font-[500] tw-px-[4px] tw-mt-[4px] tw-mb-0"
                  >
                    {addAnotherAttractionScreen.subtitle}
                  </p>
                )}
              </div>
            )}

            {appHostedAt && appHostedAt === "bosshunting" ? (
              <>
                <div className="tw-flex tw-justify-center tw-items-center">
                  {mainTrack && title === "MAIN_TRACK" && (
                    <TrackTile
                      onDoubleClick={() => handleDoubleRedirect(`/${toNavigation()}`)}
                      onClick={() => handleTrackSelection("MAIN_TRACK", mainTrackImage)}
                      isSelected={selectedTrack === TRACKS.MAIN_TRACK}
                      title={mainTrack.title}
                      titleFieldKey="banner_main_track.title"
                      description={mainTrack.description}
                      descriptionFieldKey="banner_main_track.description"
                      image={mainTrackImage}
                      videoBannerUrl={mainTrack.video_banner_url}
                      mobileVideoBannerUrl={mainTrack.mobile_video_banner_url}
                    />
                  )}

                  {miniTrack && title === "MINI_TRACK" && (
                    <TrackTile
                      onDoubleClick={() => handleDoubleRedirect(`/${toNavigation()}`)}
                      onClick={() => handleTrackSelection("MINI_TRACK", miniTrackImage)}
                      isSelected={selectedTrack === TRACKS.MINI_TRACK}
                      title={miniTrack.title}
                      titleFieldKey="banner_mini_track.title"
                      description={miniTrack.description}
                      descriptionFieldKey="banner_mini_track.description"
                      image={miniTrackImage}
                      videoBannerUrl={miniTrack.video_banner_url}
                      mobileVideoBannerUrl={miniTrack.mobile_video_banner_url}
                    />
                  )}

                  {juniorTrack && title === "JUNIOR_TRACK" && (
                    <TrackTile
                      onDoubleClick={() => handleDoubleRedirect(`/${toNavigation()}`)}
                      onClick={() => handleTrackSelection("JUNIOR_TRACK", mainTrackImage)}
                      isSelected={selectedTrack === TRACKS.JUNIOR_TRACK}
                      title={juniorTrack.title}
                      titleFieldKey="banner_junior_track.title"
                      description={juniorTrack.description}
                      descriptionFieldKey="banner_junior_track.description"
                      image={mainTrackImage}
                      videoBannerUrl={juniorTrack.video_banner_url}
                      mobileVideoBannerUrl={juniorTrack.mobile_video_banner_url}
                    />
                  )}
                </div>

                <div className="tw-bg-[#2F1160] tw-w-fit tw-mx-auto tw-py-[8px] tw-px-[16px] tw-rounded-[8px] tw-mb-[12px] tw-mt-[20px]">
                  <span className="tw-text-[white] tw-font-[700] tw-text-center tw-flex">
                    {title === "MAIN_TRACK" ? "Main Track" : title === "MINI_TRACK" ? "Mini Track" : "Junior Track"} Selected{" "}
                  </span>
                </div>

                <div className={`${windowWidth >= 640 ? "tw-flex tw-justify-center tw-items-center" : ""} tw-gap-[10px] tw-my-[15px]`}>
                  <button
                    type="button"
                    onClick={() => handleTrackSelection("MAIN_TRACK", mainTrackImage)}
                    style={{ width: "200px" }}
                    className={`tw-rounded-[100px] tw-block ${
                      windowWidth >= 640 ? "tw-mx-[0]" : "tw-mx-auto"
                    } tw-text-center tw-py-[8px] tw-px-[20px] tw-text-[18px] tw-font-bold ${
                      title === "MAIN_TRACK" ? "tw-bg-[linear-gradient(71deg,#a852ff_28.13%,#7886ff)] tw-text-white" : "tw-bg-[#393b41] tw-text-[#777777]"
                    }`}
                  >
                    Main Track
                  </button>
                  <button
                    type="button"
                    onClick={() => handleTrackSelection("MINI_TRACK", miniTrackImage)}
                    style={{ width: "200px" }}
                    className={`tw-rounded-[100px] tw-block ${
                      windowWidth >= 640 ? "tw-mx-[0] tw-my-[0]" : "tw-mx-auto"
                    } tw-text-center tw-my-[10px] tw-py-[8px] tw-px-[20px] tw-text-[18px] tw-font-bold ${
                      title === "MINI_TRACK" ? "tw-bg-[linear-gradient(71deg,#a852ff_28.13%,#7886ff)] tw-text-white" : "tw-bg-[#393b41] tw-text-[#777777]"
                    }`}
                  >
                    Mini Track
                  </button>
                </div>
              </>
            ) : (
              <DynamicFullHeightContainer footerHeight={footerHeight} containerHeightPx={containerHeightPx} setContainerHeightPx={setContainerHeightPx}>
                {/* Desktop mirrors the mobile flex-column structure so the tiles grid
                    below is bounded by the measured container height — letting the
                    desktopFill tiles shrink on short laptops instead of overflowing
                    into the container's internal scroll. */}
                <div className="tw-flex tw-flex-col tw-h-full">
                  {/* lg:tw-mb-0 on the heading: in the old block layout its 24px bottom
                      margin collapsed into the grid's 80px top margin (net gap 80px). The
                      flex-column layout no longer collapses margins, so zeroing this keeps
                      the heading-to-tiles spacing identical to before. */}
                  {!(isAnyItemsAvailableInCart && showArcadeBanner) && (trackSelectionScreen?.title || trackSelectionScreen?.subtitle) && (
                    <div className="tw-text-center tw-mt-[24px] lg:tw-mt-[40px] tw-mb-[16px] lg:tw-mb-0 sm:tw-px-4 tw-flex-shrink-0">
                      {trackSelectionScreen?.title && (
                        <h1
                          data-hlc-field="banner_general.ts_title"
                          className="tw-text-white tw-text-[24px] sm:tw-text-[28px] lg:tw-text-[32px] tw-font-extrabold tw-mb-2"
                          style={titleStyle}
                        >
                          {trackSelectionScreen.title}
                        </h1>
                      )}
                      {trackSelectionScreen?.subtitle && (
                        <p
                          data-hlc-field="banner_general.ts_subtitle"
                          className="tw-text-white max-480:tw-text-[12px] sm:tw-text-[14px] lg:tw-text-[24px] tw-font-[500] lg:tw-font-[800]"
                          style={subtitleStyle}
                        >
                          {trackSelectionScreen.subtitle}
                        </p>
                      )}
                    </div>
                  )}
                  {/* lg:auto-rows-fr + flex-grow bound the tile row to the remaining
                      column height so desktopFill tiles shrink instead of overflowing
                      into the container's internal scroll. The original lg margins are
                      kept whenever the content fits (rootGridHasRoom); only tight
                      spaces compress them to leave room for the tiles.

                      From xl the columns are pinned to the 280px tile width (and 50px
                      gap) used by the age-group tiles on /tracks, so a tile is the same
                      size on both screens; the fixed tracks are then centred with
                      justify-center since they no longer span the grid box. */}
                  <SelectorTileGrid
                    ref={tileGridRef}
                    className={`tw-flex tw-flex-col tw-flex-grow tw-justify-around tw-min-h-0 tw-gap-[10px] tw-mt-[10px] tw-mb-[10px] lg:tw-grid lg:tw-grid-cols-2 ${
                      showArcade && showBattleKart ? "xl:tw-grid-cols-[repeat(3,280px)]" : "xl:tw-grid-cols-[repeat(2,280px)]"
                    } lg:tw-gap-[20px] xl:tw-gap-[50px] xl:tw-justify-center lg:tw-min-h-0 lg:auto-rows-fr ${
                      rootGridHasRoom ? "lg:tw-mt-[80px] lg:tw-mb-[40px]" : "lg:tw-mt-[24px] lg:tw-mb-[16px]"
                    } lg:tw-max-w-[1100px] lg:tw-mx-auto`}
                  >
                    {showRacingGames && (
                      <TrackTile
                        onClick={() => handleTrackSelection("RACING_GAMES_TRACK", mainTrackImage)}
                        isSelected={false}
                        title={racingGamesTrack.title}
                        titleFieldKey="banner_racing_track.title"
                        description={racingGamesTrack.description}
                        descriptionFieldKey="banner_racing_track.description"
                        image={mainTrackImage}
                        videoBannerUrl={racingGamesTrack.video_banner_url}
                        mobileVideoBannerUrl={racingGamesTrack.mobile_video_banner_url}
                        isArcade={showArcade}
                        heightPx={useTileFill ? undefined : tileHeight}
                        fill
                        desktopFill
                      />
                    )}
                    {showBattleKart && battleKartTrack && (
                      <TrackTile
                        onClick={() => handleTrackSelection("BATTLE_KART_TRACK", mainTrackImage)}
                        isSelected={false}
                        title={battleKartTrack.title}
                        titleFieldKey="banner_battlekart_track.title"
                        description={battleKartTrack.description}
                        descriptionFieldKey="banner_battlekart_track.description"
                        image={mainTrackImage}
                        videoBannerUrl={battleKartTrack.video_banner_url}
                        mobileVideoBannerUrl={battleKartTrack.mobile_video_banner_url}
                        isArcade={showArcade}
                        heightPx={useTileFill ? undefined : tileHeight}
                        fill
                        desktopFill
                      />
                    )}
                    {showArcade && arcadeBanner && (
                      <TrackTile
                        onClick={() => handleTrackSelection("ARCADE", arcadeImage)}
                        isSelected={false}
                        title={arcadeBanner.title}
                        titleFieldKey="banner_arcade.title"
                        description={arcadeBanner.description}
                        descriptionFieldKey="banner_arcade.description"
                        image={arcadeImage}
                        videoBannerUrl={arcadeBanner.video_banner_url}
                        mobileVideoBannerUrl={arcadeBanner.mobile_video_banner_url}
                        isArcade={true}
                        heightPx={useTileFill ? undefined : tileHeight}
                        fill
                        desktopFill
                      />
                    )}
                  </SelectorTileGrid>
                </div>
              </DynamicFullHeightContainer>
            )}
          </div>
        </>
      )}

      {/* Attraction Gap Warning Popup - Shows when user has BattleKart in cart and clicks Racing, or vice versa */}
      <AttractionGapWarningPopup isPopupShowing={isAttractionGapWarningShowing} handleClosePopup={handleCloseAttractionGapWarning} currentTrack={pendingTrackSelection?.track} />
    </>
  );
};

export default TrackSelection;
